import { useSearchParams } from 'react-router-dom'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, Grid, Tooltip, Typography } from '@mui/material'
// components
import { CheckModal } from './CheckModal'
import { Alert } from '../../common/Alert'
import { EmptyData } from '../../common/EmptyData'
import AccountsPayableListing from './PayablesListing'
import ClientDropdown from '../../common/ClientDropdown'
import { CardBox } from '../../../theme/styleComponents'
import AccountDropdown from '../../common/AccountDropdown'
import SelectController from '../../common/SelectController'
import AddAccountPayableModal from './AddAccountPayableModal'
import { CustomController } from '../../common/CustomController'
import OfficeAutoComplete from '../../offices/OfficeAutocomplete'
import VendorAutoComplete from '../../vendors/VendorAutoComplete'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import MatterDropdownWithForm from '../../common/MatterDropdownWithForm'
import { CustomDatePickerController } from '../../common/CustomDatePickerRController'
// others
import { AppContext } from '../../../context'
import { enumToLabel, formatCurrency } from '../../../utils'
import { VoidPayableDialog } from './VoidPayableDialog'
import { ActionType } from '../../../context/AppContextReducer'
import { ApprovedCheckAssignment, printChecks } from '../../../../services'
import { AccountOption, AutoCompleteOption, Nullable } from '../../../interfaceTypes/common'
import { FetchPayablesInput, ProcessPayableFormValues } from '../../../interfaceTypes/forms'
import {
  VoidFilter,
  PrintFilter,
  PaymentMethod,
  PayableStatus,
  AccountsPayable,
  useSoftDeletePayableMutation,
  useFetchAccountsPayablesLazyQuery,
  useVoidAndDuplicatePayableMutation
} from '../../../../generated'
import {
  QUERY_PARAMS,
  MIN_SEARCH_LENGTH,
  PAYABLES_PAGE_LIMIT,
  GRAPHQL_QUERY_POLICY,
  SOMETHING_WENT_WRONG,
  PAYABLE_ENTRY_PAYMENT_METHODS
} from '../../../constants'

const PRINT_FILTER_OPTIONS = [
  { label: 'All', value: PrintFilter.All },
  { label: 'Printed', value: PrintFilter.Printed },
  { label: 'Non-Printed', value: PrintFilter.NonPrinted }
]

const AccountPayable: FC = () => {
  const { dispatch, currentPage } = useContext(AppContext)

  const [searchParams, setSearchParams] = useSearchParams()

  const [openPayable, setOpenPayable] = useState(false)
  const [openCheckModal, setOpenCheckModal] = useState(false)
  // Set when a single row's Print Check is used. The tick-box selection is deliberately left alone —
  // a per-row print must not silently redefine a batch the user was still assembling.
  const [payableToPrint, setPayableToPrint] = useState<Nullable<AccountsPayable>>(null)
  const [previewingVoucher, setPreviewingVoucher] = useState<Nullable<string>>(null)
  const [editPayable, setEditPayable] = useState<Nullable<AccountsPayable>>(null)
  const [payableToVoid, setPayableToVoid] = useState<Nullable<AccountsPayable>>(null)
  const [payableToVoidAndDuplicate, setPayableToVoidAndDuplicate] = useState<Nullable<AccountsPayable>>(null)
  // Shown inside the open confirmation modal instead of as a toast: the void guards return an
  // explanation of what to do instead, which is far too long for a snackbar.
  const [actionError, setActionError] = useState<Nullable<string>>(null)
  const [payableToDelete, setPayableToDelete] = useState<Nullable<AccountsPayable>>(null)

  const [payables, setPayables] = useState<AccountsPayable[]>([])
  // Total of the vouchers on the page currently on screen, so it always ties to the rows the user
  // can see and add up. The payload still carries a whole-filter total; it is deliberately unused.
  const totalAmount = useMemo(() => payables.reduce((sum, payable) => sum + (payable?.amount || 0), 0), [payables])
  const [filter, setFilter] = useState<Partial<FetchPayablesInput>>({})
  const selectedPayables = searchParams.get(QUERY_PARAMS.SELECTED_PAYABLES)?.split(',')
  const matterIdFromUrl = searchParams.get('matterId') ?? undefined

  const handleOpenPayable = () => setOpenPayable(true)
  const handleClosePayable = () => setOpenPayable(false)

  const methods = useForm<FetchPayablesInput>({
    defaultValues: {
      voidFilter: VoidFilter.All,
      printFilter: PrintFilter.All,
      unpaidOnly: false,
      paginationOptions: {
        page: currentPage,
        limit: PAYABLES_PAGE_LIMIT
      }
    }
  })

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = methods

  const [fetchAccountPayables, { loading, refetch }] = useFetchAccountsPayablesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      setPayables([])
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchAccountsPayables } = data || {}
      const { pagination, apEntries } = fetchAccountsPayables || {}
      setPayables(apEntries.length > 0 ? (apEntries as AccountsPayable[]) : [])
      if (pagination.totalPages) {
        dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: pagination.totalPages || 1 })
        dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: pagination.totalCount || 1 })
      }
    }
  })

  const [softDeletePayableMutation, { loading: deletingPayable }] = useSoftDeletePayableMutation({
    onError() {
      Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { softDeletePayable } = data || {}
      const { response } = softDeletePayable || {}
      const { status } = response || {}
      if (status === 200) {
        Alert.success('Deleted successfully')
        setPayableToDelete(null)
        refetch?.()
      }
    }
  })

  const [voidAndDuplicatePayableMutation, { loading: voidingAndDuplicating }] = useVoidAndDuplicatePayableMutation({
    onError(error) {
      setActionError(error.message || SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { voidAndDuplicatePayable } = data || {}
      const { response, duplicatePayable: duplicate } = voidAndDuplicatePayable || {}
      const { status } = response || {}
      if (status === 200 && duplicate?.voucherNo) {
        Alert.success(`Voided and duplicated as ${duplicate.voucherNo}`)
        setPayableToVoidAndDuplicate(null)
        refetch?.()
      }
    }
  })

  // Any filter change re-runs the query, so the vouchers that were ticked may not even be in the new
  // result set — drop the selection (and let the caller adjust other params in the same URL update, so
  // one write does not clobber the other).
  const clearSelectedPayables = (mutateParams?: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams)
    next.delete(QUERY_PARAMS.SELECTED_PAYABLES)
    mutateParams?.(next)
    if (next.toString() !== searchParams.toString()) setSearchParams(next)
  }

  // Drop a single voucher from the selection, used by the check-printing modal so an unprintable
  // payable can be taken out without closing the modal and starting over. The URL owns the selection,
  // so writing it here also unticks the row behind the modal — one source of truth, and the modal's
  // rescan picks up the shorter run and renumbers it.
  const deselectPayable = (voucherNo: string) => {
    const remaining = selectedVoucherNos.filter(selected => selected !== voucherNo)
    const next = new URLSearchParams(searchParams)
    if (!remaining.length) next.delete(QUERY_PARAMS.SELECTED_PAYABLES)
    else next.set(QUERY_PARAMS.SELECTED_PAYABLES, remaining.join(','))
    setSearchParams(next)
  }

  const onSubmit = async (data: FetchPayablesInput) => {
    clearSelectedPayables()
    const {
      account,
      glAccount,
      invoiceDateFrom,
      invoiceDateTo,
      office,
      vendor,
      keyword,
      checkNumber,
      voidFilter,
      printFilter,
      unpaidOnly,
      ...rest
    } = data || {}
    const input = {
      bankAccountId: account?.value,
      glAccountId: glAccount?.value,
      invoiceDateFrom: invoiceDateFrom || undefined,
      invoiceDateTo: invoiceDateTo || undefined,
      officeId: office?.value,
      vendorId: vendor?.value,
      keyword: keyword,
      checkNumber: checkNumber?.trim() || undefined,
      voidFilter: voidFilter as VoidFilter | undefined,
      printFilter: printFilter as PrintFilter | undefined,
      status: unpaidOnly ? PayableStatus.Unpaid : undefined,
      ...rest
    }
    setFilter(data)
    await fetchAccountPayables({
      variables: {
        input
      }
    })
  }

  const actionMethod = useForm<ProcessPayableFormValues>({
    mode: 'onChange'
  })
  // Read during render so the component is subscribed to the field, which is what drives both the
  // dropdown's controlled value and the effect below.
  const selectedAction = actionMethod.watch('action')

  const closeCheckModal = () => {
    actionMethod.setValue('action', '')
    setPayableToPrint(null)
    return setOpenCheckModal(false)
  }

  // Pre-fill data for the check modal, derived from the selected payables: their distinct bank
  // accounts (to pre-fill the account / warn on a multi-bank selection), and — when a single payable
  // is selected and already has a check number — that check number (staff sometimes assign it up front).
  const {
    vouchers: selectedVoucherNos,
    bankAccounts: selectedBankAccounts,
    checkNo: defaultCheckNo
  } = useMemo<{
    vouchers: string[]
    bankAccounts: AccountOption[]
    checkNo: string
  }>(() => {
    if (!selectedPayables?.length) return { vouchers: [], bankAccounts: [], checkNo: '' }
    const areAllSelected = selectedPayables.length === 1 && selectedPayables[0] === QUERY_PARAMS.ALL
    const chosen = areAllSelected ? payables : payables.filter(p => selectedPayables.includes(p.voucherNo))
    const byId = new Map<string, AccountOption>()
    chosen.forEach(p => {
      if (p.account?.id) byId.set(p.account.id, { value: p.account.id, label: p.account.name || '' })
    })
    return {
      // Resolved here rather than at submit time so the confirmation table and the print request are
      // driven by the same list — the table would be worthless if it described a different run.
      vouchers: chosen.map(p => p.voucherNo),
      bankAccounts: Array.from(byId.values()),
      checkNo: chosen.length === 1 ? chosen[0].checkOrAchNumber ?? '' : ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payables, searchParams])

  /**
   * What the check modal and the print request actually operate on. A row's Print Check narrows the
   * run to that one voucher; otherwise it is whatever is ticked, exactly as before.
   */
  const checkRun = useMemo(() => {
    if (!payableToPrint) {
      return { vouchers: selectedVoucherNos, bankAccounts: selectedBankAccounts, checkNo: defaultCheckNo }
    }

    return {
      vouchers: [payableToPrint.voucherNo],
      bankAccounts: payableToPrint.account?.id
        ? [{ value: payableToPrint.account.id, label: payableToPrint.account.name || '' }]
        : [],
      checkNo: payableToPrint.checkOrAchNumber ?? ''
    }
  }, [payableToPrint, selectedVoucherNos, selectedBankAccounts, defaultCheckNo])

  /**
   * Preview draws the check onto plain paper: no leaf is consumed, nothing is written and the payable
   * stays unpaid, so it skips the modal the same way the bulk Preview Checks action does.
   */
  const handleRowPreviewCheck = async (payable: AccountsPayable) => {
    setPreviewingVoucher(payable.voucherNo)
    try {
      await printChecks([payable.voucherNo], true)
    } catch (error) {
      if (error instanceof Error) Alert.error('Error: ' + error.message)
    } finally {
      setPreviewingVoucher(null)
    }
  }

  const handleRowPrintCheck = (payable: AccountsPayable) => {
    setPayableToPrint(payable)
    setOpenCheckModal(true)
  }

  const onActionSubmit = useCallback(
    async (
      data: ProcessPayableFormValues,
      startingCheckNo?: string,
      bankAccountId?: string,
      assignments?: ApprovedCheckAssignment[]
    ) => {
      const { action } = data
      const { vouchers } = checkRun

      if (vouchers.length > 0) {
        const isPreview = action.includes('preview')
        try {
          await printChecks(vouchers, isPreview, startingCheckNo, bankAccountId, assignments)
          if (!isPreview) refetch?.()
        } catch (error) {
          console.log(error)
          if (error instanceof Error) Alert.error('Error: ' + error?.message)
        } finally {
          actionMethod.setValue('action', '')
        }
      } else Alert.error('Please select at least one voucher')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkRun]
  )

  // When landing with matterId in URL, set filter and form so fetch uses it
  useEffect(() => {
    if (!matterIdFromUrl) return
    if (filter?.matter?.value === matterIdFromUrl) return
    setFilter(prev => ({
      ...prev,
      matter: { value: matterIdFromUrl, label: '' }
    }))
    setValue('matter', { value: matterIdFromUrl, label: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterIdFromUrl])

  useEffect(() => {
    setValue('paginationOptions.page', currentPage)

    const {
      account,
      glAccount,
      invoiceDateFrom,
      invoiceDateTo,
      office,
      vendor,
      keyword,
      checkNumber,
      voucherType,
      voidFilter,
      printFilter,
      unpaidOnly,
      matter,
      client
    } = filter || {}
    const matterId = matter?.value ?? matterIdFromUrl
    const input = {
      bankAccountId: account?.value,
      glAccountId: glAccount?.value,
      invoiceDateFrom: invoiceDateFrom || undefined,
      invoiceDateTo: invoiceDateTo || undefined,
      officeId: office?.value,
      vendorId: vendor?.value,
      keyword,
      checkNumber: checkNumber?.trim() || undefined,
      voucherType,
      voidFilter: voidFilter as VoidFilter | undefined,
      printFilter: printFilter as PrintFilter | undefined,
      status: unpaidOnly ? PayableStatus.Unpaid : undefined,
      matterId,
      clientId: client?.value
    }
    fetchAccountPayables({
      variables: {
        input: {
          ...input,
          paginationOptions: {
            page: currentPage,
            limit: PAYABLES_PAGE_LIMIT
          }
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, setValue, filter, matterIdFromUrl])

  // Paging swaps the whole result set the same way a filter does, and "all" means "all rows on this
  // page" — carrying the ticks over would print vouchers the user can no longer see. The page lives in
  // AppContext (the pagination control is shared), so watch it instead of hooking a change handler.
  const previousPageRef = useRef(currentPage)
  useEffect(() => {
    if (previousPageRef.current === currentPage) return
    previousPageRef.current = currentPage
    actionMethod.setValue('action', '')
    clearSelectedPayables()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleUnpaidOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setValue('unpaidOnly', checked)
    setFilter(prev => ({ ...prev, unpaidOnly: checked }))
    actionMethod.setValue('action', '')
    clearSelectedPayables()
  }

  // Fires on the transition into an action, not on every render: `onActionSubmit` used to be rebuilt
  // each render (its dep array held a freshly-split array), and this effect quietly depended on that
  // churn to notice a new selection.
  useEffect(() => {
    if (!selectedAction) return
    if (selectedAction === 'print-check') setOpenCheckModal(true)
    else onActionSubmit({ action: selectedAction })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAction])

  const handleFilterChange = (name: string, option: any, mutateParams?: (params: URLSearchParams) => void) => {
    setFilter(prev => ({
      ...prev,
      [name]:
        name === 'voucherType' || name === 'voidFilter' || name === 'printFilter'
          ? option?.value && option.value !== VoidFilter.All
            ? option.value
            : undefined
          : option ?? undefined
    }))
    actionMethod.setValue('action', '')
    clearSelectedPayables(mutateParams)
  }

  const handleAccountChange = (option: AccountOption | null) => handleFilterChange('account', option)
  const handleGlAccountChange = (option: AccountOption | null) => handleFilterChange('glAccount', option)
  const handleOfficeChange = (option: AutoCompleteOption | null) => handleFilterChange('office', option)
  const handleVendorChange = (option: AutoCompleteOption | null) => handleFilterChange('vendor', option)
  const handleMatterChange = (option: AccountOption | null) =>
    handleFilterChange('matter', option, params => {
      if (option?.value) {
        params.set('matterId', option.value)
      } else {
        params.delete('matterId')
      }
    })
  const handleClientChange = (option: AccountOption | null) => handleFilterChange('client', option)
  const keywordDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const checkNumberDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePaymentChange = (option: AccountOption | null) => handleFilterChange('voucherType', option)
  const handleVoidFilterChange = (option: AccountOption | null) => handleFilterChange('voidFilter', option)
  const handlePrintFilterChange = (option: AccountOption | null) => handleFilterChange('printFilter', option)
  const handleParams = (option: string | null, name: string) => {
    if (name === 'keyword') {
      if (keywordDebounceRef.current) clearTimeout(keywordDebounceRef.current)
      if (option && option.length < MIN_SEARCH_LENGTH) return
      keywordDebounceRef.current = setTimeout(() => handleFilterChange(name, option), 1000)
    } else if (name === 'checkNumber') {
      // Debounced like the wide search, but with NO minimum length — check numbers are matched whole and can
      // legitimately be a single digit (the shortest in the ledger is 1 character).
      if (checkNumberDebounceRef.current) clearTimeout(checkNumberDebounceRef.current)
      checkNumberDebounceRef.current = setTimeout(() => handleFilterChange(name, option), 1000)
    } else {
      handleFilterChange(name, option)
    }
  }

  return (
    <>
      <CardBox marginTop={3}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} px={3} py={1}>
              <Typography variant="h6">Accounts Payables</Typography>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                <Button type="button" variant="contained" onClick={handleOpenPayable}>
                  Add Account Payable
                </Button>
              </Box>
            </Box>

            <Divider />

            <Grid container spacing={3} rowSpacing={1} p={3}>
              <Grid item md={3} sm={12} xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <Controller
                    name="account"
                    control={control}
                    render={({ field }) => (
                      <AccountDropdown
                        value={field.value}
                        onChange={option => {
                          field.onChange(option)
                          handleAccountChange(option)
                        }}
                        searchFilters={{
                          isBankAccount: true
                        }}
                        label={'Bank Account'}
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <OfficeAutoComplete
                  name="office"
                  label="Profit Center"
                  customOnChange={handleOfficeChange}
                  autoSelectOnTab
                />
              </Grid>

              <Grid item md={3} xs={12}>
                <VendorAutoComplete
                  name="vendor"
                  label={'Vendor'}
                  errorMessage={errors.vendor?.message?.toString()}
                  customOnChange={handleVendorChange}
                />
              </Grid>

              <Grid item md={3} xs={12} mt={1}>
                <MatterDropdownWithForm
                  name="matter"
                  label={'Matter'}
                  userId=""
                  customOnChange={handleMatterChange}
                  autoSelectOnTab
                />
              </Grid>

              <Grid item md={3} xs={12} mt={1}>
                <FormControl variant="outlined" fullWidth>
                  <Controller
                    name="glAccount"
                    control={control}
                    render={({ field }) => (
                      <AccountDropdown
                        value={field.value}
                        onChange={option => {
                          field.onChange(option)
                          handleGlAccountChange(option)
                        }}
                        searchFilters={{ isBankAccount: false }}
                        label="GL Account"
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item md={3} xs={12} mt={1}>
                <Controller
                  name="client"
                  control={control}
                  render={({ field }) => (
                    <ClientDropdown
                      selectedClientId={field.value?.value ?? undefined}
                      autoSelectOnTab
                      onChange={option => {
                        const clientOption = {
                          label: `${(option?.firstName || '') + (option?.lastName || '')}`,
                          value: option?.id || ''
                        }
                        field.onChange(clientOption)
                        handleClientChange(clientOption)
                      }}
                      label={'Client'}
                    />
                  )}
                />
              </Grid>

              <Grid item md={3} xs={12} sm={6} mt={1}>
                <SelectController
                  name="voidFilter"
                  label="Voided Filter"
                  options={[
                    { label: 'All', value: VoidFilter.All },
                    { label: 'Voided Only', value: VoidFilter.VoidedOnly },
                    { label: 'Non-Voided', value: VoidFilter.NonVoided }
                  ]}
                  customOnChange={handleVoidFilterChange}
                />
              </Grid>

              <Grid item md={3} xs={12} sm={6} mt={1}>
                <SelectController
                  name="voucherType"
                  label="Payment Type"
                  clearable
                  options={Object.values(PAYABLE_ENTRY_PAYMENT_METHODS).map(method => ({
                    label: method === PaymentMethod.Cheque ? enumToLabel('Check') : enumToLabel(method),
                    value: method.toLocaleLowerCase()
                  }))}
                  customOnChange={handlePaymentChange}
                />
              </Grid>

              <Grid item md={3} xs={12} sm={12}>
                <CustomDatePickerController
                  controllerName="invoiceDateFrom"
                  controllerLabel="Invoice Date From"
                  placeholder="Invoice Date From"
                  setParams={handleParams}
                />
              </Grid>

              <Grid item md={3} xs={12} sm={12}>
                <CustomDatePickerController
                  controllerName="invoiceDateTo"
                  controllerLabel="Invoice Date To"
                  placeholder="Invoice Date To"
                  setParams={handleParams}
                />
              </Grid>

              <Grid item md={3} xs={12} mt={0}>
                <CustomController
                  controllerName="checkNumber"
                  controllerLabel="Check / Ref No"
                  placeholder="Exact check number"
                  setParams={handleParams}
                />
              </Grid>

              <Grid item md={3} xs={12} mt={0}>
                <CustomController
                  controllerName="keyword"
                  controllerLabel="Search"
                  placeholder="Voucher No, Check No, Notes"
                  setParams={handleParams}
                />
              </Grid>
              <Grid item md={3} xs={12} sm={6} mt={1}>
                <SelectController
                  name="printFilter"
                  label="Print Status"
                  options={PRINT_FILTER_OPTIONS}
                  customOnChange={handlePrintFilterChange}
                />
              </Grid>

              <Grid item md={3} xs={12} sm={6} display="flex" alignItems="center">
                <Controller
                  name="unpaidOnly"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={e => {
                            field.onChange(e.target.checked)
                            handleUnpaidOnlyChange(e)
                          }}
                          size="small"
                        />
                      }
                      label="Show Unpaid Only"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </CardBox>

      <CardBox marginTop={3} pt={2} pb={payables.length > 0 ? 0 : 3}>
        {payables.length > 0 ? (
          <>
            <FormProvider {...actionMethod}>
              <form onSubmit={actionMethod.handleSubmit((data, event) => onActionSubmit(data))}>
                <Grid container spacing={3} p={2} mb={2}>
                  <Grid item md={9} xs={12} sm={6} display="flex" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                        Total Amount
                      </Typography>
                      <Tooltip
                        title={`Sum of the ${payables.length} voucher(s) on this page`}
                        arrow
                        placement="top-start"
                      >
                        <Typography variant="h6" component="span">
                          {formatCurrency(totalAmount)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Grid>

                  <Grid item md={3} xs={12} sm={6}>
                    <SelectController
                      name="action"
                      label="Action"
                      options={[
                        { label: 'None', value: '' },
                        { label: 'Print Checks', value: 'print-check' },
                        { label: 'Preview Checks', value: 'preview-check' }
                      ]}
                      required
                      value={
                        selectedAction === ''
                          ? {
                              label: 'None',
                              value: ''
                            }
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </form>
            </FormProvider>

            <AccountsPayableListing
              loading={loading}
              payables={payables}
              onEdit={payable => setEditPayable(payable)}
              onVoid={payable => {
                setActionError(null)
                setPayableToVoid(payable)
              }}
              onVoidAndDuplicate={payable => {
                setActionError(null)
                setPayableToVoidAndDuplicate(payable)
              }}
              onDelete={payable => setPayableToDelete(payable)}
              onPreviewCheck={handleRowPreviewCheck}
              onPrintCheck={handleRowPrintCheck}
              previewingVoucher={previewingVoucher}
            />
          </>
        ) : (
          <EmptyData title="No Checks!" iconSize="100px" />
        )}
      </CardBox>

      {openPayable && (
        <AddAccountPayableModal isOpen={openPayable} handleClose={handleClosePayable} refetch={refetch} />
      )}
      {editPayable && (
        <AddAccountPayableModal
          isOpen={!!editPayable}
          handleClose={() => setEditPayable(null)}
          currentPayable={editPayable}
          refetch={refetch}
        />
      )}
      <CheckModal
        isOpen={openCheckModal}
        handleClose={closeCheckModal}
        startPrinting={onActionSubmit}
        vouchers={checkRun.vouchers}
        // A single-row run has nothing left to print once its only row goes, so removing it closes the
        // modal rather than deselecting a payable the user never ticked.
        onRemoveVoucher={payableToPrint ? closeCheckModal : deselectPayable}
        bankAccounts={checkRun.bankAccounts}
        defaultCheckNo={checkRun.checkNo}
      />
      {payableToVoid && (
        <VoidPayableDialog
          payable={payableToVoid}
          onClose={() => setPayableToVoid(null)}
          onVoided={() => {
            Alert.success('Voided successfully')
            setPayableToVoid(null)
            refetch?.()
          }}
        />
      )}
      {payableToDelete && (
        <ConfirmationModal
          isOpen={!!payableToDelete}
          title="Delete Payable"
          message={`Are you sure you want to delete ${payableToDelete.voucherNo}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={deletingPayable}
          variant="danger"
          onConfirm={() => softDeletePayableMutation({ variables: { id: payableToDelete.id } })}
          onCancel={() => setPayableToDelete(null)}
        />
      )}
      {payableToVoidAndDuplicate && (
        <ConfirmationModal
          isOpen={!!payableToVoidAndDuplicate}
          title="Void & Duplicate"
          message={`This will void ${payableToVoidAndDuplicate.voucherNo} (deleting all related bank payments, expense and journal entries) and create an unpaid duplicate with all line items. Are you sure?`}
          confirmText="Void & Duplicate"
          cancelText="Cancel"
          isLoading={voidingAndDuplicating}
          variant="danger"
          onConfirm={() => voidAndDuplicatePayableMutation({ variables: { id: payableToVoidAndDuplicate.id } })}
          onCancel={() => {
            setActionError(null)
            setPayableToVoidAndDuplicate(null)
          }}
          error={actionError}
        />
      )}
    </>
  )
}

export default AccountPayable
